import { ArrowLeft, Bell, CheckCircle2, Copy, CreditCard, Download, ExternalLink, FileText, MessageSquareText, RefreshCw, Search, Settings2, SlidersHorizontal, XCircle, Zap } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../components/admin-layout';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Pagination } from '../../components/ui/pagination';
import { Select } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import useDebounce from '../../hooks/useDebounce';
import { downloadAgentBuildRequestAttachment, getAgentBuildRequest, getAgentBuildRequestSettings, listAgentBuildRequests, retryAgentBuildRequestNotification, updateAgentBuildRequest, updateAgentBuildRequestSettings } from '../../services/agent-build-request';

const STATUSES = [
  ['PENDING', 'Pendente'],
  ['IN_REVIEW', 'Em análise'],
  ['NEEDS_INFORMATION', 'Aguardando informações'],
  ['APPROVED', 'Aprovada'],
  ['IN_PROGRESS', 'Em construção'],
  ['COMPLETED', 'Concluída'],
  ['REJECTED', 'Rejeitada'],
  ['CANCELED', 'Cancelada'],
];

const STATUS_STYLE = {
  PENDING: 'border-amber-400/20 bg-amber-400/10 text-amber-600',
  IN_REVIEW: 'border-sky-400/20 bg-sky-400/10 text-sky-500',
  NEEDS_INFORMATION: 'border-orange-400/20 bg-orange-400/10 text-orange-500',
  APPROVED: 'border-emerald-400/20 bg-emerald-400/10 text-emerald-500',
  IN_PROGRESS: 'border-violet-400/20 bg-violet-400/10 text-violet-500',
  COMPLETED: 'border-teal-400/20 bg-teal-400/10 text-teal-500',
  REJECTED: 'border-rose-400/20 bg-rose-400/10 text-rose-500',
  CANCELED: 'border-slate-400/20 bg-slate-400/10 text-slate-500',
};

const CHANNEL_LABELS = {
  WHATSAPP: 'WhatsApp',
  WEB_CHAT: 'Chat no site',
  INSTAGRAM: 'Instagram',
  VOICE: 'Voz',
  API: 'API',
  OTHER: 'Outro',
};
const INTEGRATION_LABELS = {
  CRM: 'CRM',
  CALENDAR: 'Agenda',
  ERP: 'ERP',
  ECOMMERCE: 'E-commerce',
  PAYMENTS: 'Pagamentos',
  HUMAN_HANDOFF: 'Atendimento humano',
  OTHER: 'Outra',
};

function unwrap(response) {
  return response?.data ?? response;
}
function errorMessage(error, fallback) {
  return error?.data?.message || error?.data?.errorMessage || error?.message || fallback;
}

export default function AgentRequests() {
  return (
    <Routes>
      <Route
        index
        element={
          <AdminLayout noPadding withBackground={false}>
            <AgentRequestList />
          </AdminLayout>
        }
      />
      <Route
        path=":id"
        element={
          <AdminLayout noPadding withBackground={false}>
            <AgentRequestDetail />
          </AdminLayout>
        }
      />
    </Routes>
  );
}

function AgentRequestList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [queue, setQueue] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const requestSequence = useRef(0);
  const debouncedSearch = useDebounce(search, 250);
  const perPage = 20;

  useEffect(() => {
    const sequence = ++requestSequence.current;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await listAgentBuildRequests({
          page,
          perPage,
          search: debouncedSearch,
          status,
          queue,
        });
        if (sequence !== requestSequence.current) return;
        setItems(response?.data || []);
        setTotal(response?.total || 0);
      } catch (requestError) {
        if (sequence !== requestSequence.current) return;
        setError(errorMessage(requestError, 'Não foi possível listar as solicitações.'));
      } finally {
        if (sequence === requestSequence.current) setLoading(false);
      }
    };
    load();
  }, [page, debouncedSearch, status, queue]);

  return (
    <div className="min-h-full bg-[var(--dash-bg)] p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-7 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--dash-primary)]">Novos projetos</p>
            <h1 className="text-2xl font-semibold tracking-tight text-[var(--dash-text)] sm:text-3xl">Solicitações de agentes</h1>
            <p className="mt-2 text-sm text-[var(--dash-text-muted)]">Briefings enviados por empresas interessadas em construir agentes personalizados.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" leftIcon={Settings2} onClick={() => setSettingsOpen(true)}>
              Configurar prioridade
            </Button>
            <div className="flex items-center gap-2 rounded-xl border border-[var(--dash-border)] bg-[var(--dash-surface)] px-4 py-2 text-xs text-[var(--dash-text-muted)]">
              <MessageSquareText size={15} /> {total} solicitação
              {total === 1 ? '' : 'ões'}
            </div>
          </div>
        </div>
        <div className="mb-5 grid gap-3 rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface)] p-3 shadow-[var(--dash-shadow-sm)] md:grid-cols-[1fr_220px_220px]">
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            leftIcon={Search}
            placeholder="Buscar solicitante, empresa, e-mail ou agente"
          />
          <Select
            value={status}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
            }}
            leftIcon={SlidersHorizontal}
            options={[{ value: '', label: 'Todos os status' }, ...STATUSES.map(([value, label]) => ({ value, label }))]}
          />
          <Select
            value={queue}
            onChange={(value) => {
              setQueue(value);
              setPage(1);
            }}
            leftIcon={Zap}
            options={[
              { value: '', label: 'Todas as filas' },
              { value: 'PRIORITY', label: 'Prioritária paga' },
              { value: 'FREE', label: 'Gratuita' },
              { value: 'WAITING_PAYMENT', label: 'Aguardando pagamento' },
            ]}
          />
        </div>
        {error && <ErrorBox>{error}</ErrorBox>}
        <div className="overflow-hidden rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface)] shadow-[var(--dash-shadow-sm)]">
          {loading ? (
            <div className="space-y-px">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="h-20 animate-pulse bg-[var(--dash-surface-subtle)]" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="px-6 py-20 text-center">
              <MessageSquareText className="mx-auto mb-4 text-[var(--dash-text-faint)]" size={34} />
              <h2 className="text-sm font-semibold text-[var(--dash-text)]">Nenhuma solicitação encontrada</h2>
              <p className="mt-2 text-xs text-[var(--dash-text-muted)]">Altere os filtros ou aguarde a chegada de um novo briefing.</p>
            </div>
          ) : (
            <div className="divide-y divide-[var(--dash-border)]">
              {items.map((item) => (
                <button key={item.id} onClick={() => navigate(`/agent-requests/${item.id}`)} className="grid w-full gap-3 px-5 py-5 text-left transition hover:bg-[var(--dash-surface-subtle)] md:grid-cols-[1.3fr_1fr_1fr_auto] md:items-center">
                  <div>
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <strong className="text-sm font-semibold text-[var(--dash-text)]">{item.agentName}</strong>
                      <QueueBadge request={item} />
                    </div>
                    <span className="block text-xs text-[var(--dash-text-muted)]">
                      {item.requesterName} · {item.requesterEmail}
                    </span>
                    <span className="mt-2 block text-[10px] font-medium text-[var(--dash-primary)]">{(typeof item.channels === 'string' ? JSON.parse(item.channels) : item.channels || []).map((channel) => CHANNEL_LABELS[channel] || channel).join(' · ')}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase tracking-[0.14em] text-[var(--dash-text-faint)]">Empresa</span>
                    <span className="mt-1 block text-xs font-medium text-[var(--dash-text-soft)]">{item.companyName}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase tracking-[0.14em] text-[var(--dash-text-faint)]">{item.queuedAt ? 'Na fila desde' : 'Recebida'}</span>
                    <span className="mt-1 block text-xs font-medium text-[var(--dash-text-soft)]">{new Date(item.queuedAt || item.submittedAt).toLocaleDateString('pt-BR')}</span>
                  </div>
                  <Status status={item.status} />
                </button>
              ))}
            </div>
          )}
        </div>
        <Pagination page={page} pageSize={perPage} total={total} onPageChange={setPage} className="mt-2" />
        <PrioritySettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      </div>
    </div>
  );
}

function AgentRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [retrying, setRetrying] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  useEffect(() => {
    (async () => {
      try {
        const data = unwrap(await getAgentBuildRequest(id));
        setRequest(data);
        setStatus(data.status);
        setNotes(data.adminNotes || '');
      } catch (requestError) {
        setError(errorMessage(requestError, 'Não foi possível carregar a solicitação.'));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);
  const channels = useMemo(() => {
    try {
      return typeof request?.channels === 'string' ? JSON.parse(request.channels) : request?.channels || [];
    } catch {
      return [];
    }
  }, [request]);
  const knowledgeUrls = useMemo(() => {
    try {
      return typeof request?.knowledgeUrls === 'string' ? JSON.parse(request.knowledgeUrls) : request?.knowledgeUrls || [];
    } catch {
      return [];
    }
  }, [request]);
  const trackingUrl = request?.trackingId ? `${window.location.origin}/lab/atendimento/${request.trackingId}` : '';
  const saveStatus = async () => {
    setSavingStatus(true);
    setError('');
    setSuccess('');
    try {
      const updated = unwrap(await updateAgentBuildRequest(id, { status }));
      setRequest(updated);
      setStatus(updated.status);
      setSuccess(status === request.status ? 'O status não mudou; nenhuma notificação foi enviada.' : 'Status atualizado. O envio por WhatsApp e e-mail foi processado.');
    } catch (requestError) {
      setError(errorMessage(requestError, 'Não foi possível atualizar o status.'));
    } finally {
      setSavingStatus(false);
    }
  };
  const saveNotes = async () => {
    setSavingNotes(true);
    setError('');
    setSuccess('');
    try {
      const updated = unwrap(await updateAgentBuildRequest(id, { adminNotes: notes }));
      setRequest(updated);
      setNotes(updated.adminNotes || '');
      setSuccess('Notas internas salvas sem notificar o cliente.');
    } catch (requestError) {
      setError(errorMessage(requestError, 'Não foi possível salvar as notas.'));
    } finally {
      setSavingNotes(false);
    }
  };
  const retryNotification = async (notificationId) => {
    setRetrying(notificationId);
    setError('');
    setSuccess('');
    try {
      const updated = unwrap(await retryAgentBuildRequestNotification(id, notificationId));
      setRequest(updated);
      setSuccess('Reenvio processado. Confira o resultado abaixo.');
    } catch (requestError) {
      setError(errorMessage(requestError, 'Não foi possível reenviar a notificação.'));
    } finally {
      setRetrying(null);
    }
  };
  const download = async (attachment) => {
    try {
      const blob = await downloadAgentBuildRequestAttachment(id, attachment.id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = attachment.originalName;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(errorMessage(requestError, 'Não foi possível baixar o anexo.'));
    }
  };
  if (loading)
    return (
      <div className="min-h-full bg-[var(--dash-bg)] p-8">
        <div className="mx-auto h-96 max-w-6xl animate-pulse rounded-3xl bg-[var(--dash-surface)]" />
      </div>
    );
  if (!request)
    return (
      <div className="min-h-full bg-[var(--dash-bg)] p-8">
        <div className="mx-auto max-w-6xl">
          <ErrorBox>{error}</ErrorBox>
          <Button className="mt-4" variant="secondary" onClick={() => navigate('/agent-requests')}>
            Voltar
          </Button>
        </div>
      </div>
    );
  return (
    <div className="min-h-full bg-[var(--dash-bg)] p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-6xl">
        <button onClick={() => navigate('/agent-requests')} className="mb-6 flex items-center gap-2 text-xs font-medium text-[var(--dash-text-muted)] hover:text-[var(--dash-text)]">
          <ArrowLeft size={14} /> Voltar para solicitações
        </button>
        <div className="mb-7 flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="mb-3 flex items-center gap-3">
              <Status status={request.status} />
              <span className="text-xs text-[var(--dash-text-faint)]">#{request.id}</span>
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-[var(--dash-text)] sm:text-4xl">{request.agentName}</h1>
            <p className="mt-3 text-sm text-[var(--dash-text-muted)]">
              Solicitada por {request.requesterName} para {request.companyName} em {new Date(request.submittedAt).toLocaleString('pt-BR')}.
            </p>
          </div>
        </div>
        {error && <ErrorBox>{error}</ErrorBox>}
        {success && <div className="mb-5 rounded-xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3 text-xs text-emerald-600">{success}</div>}
        <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
          <div className="space-y-6">
            <Section title="Solicitante e empresa">
              <InfoGrid
                items={[
                  ['Nome', request.requesterName],
                  ['E-mail', request.requesterEmail],
                  ['WhatsApp', request.requesterPhone],
                  ['Cargo', request.requesterRole || 'Não informado'],
                  ['Empresa', request.companyName],
                  ['Segmento', request.companySegment],
                  ['Site', request.companyWebsite || 'Não informado'],
                  ['Conversas/mês', request.monthlyConversationVolume ?? 'Não informado'],
                ]}
              />
            </Section>
            <Section title="Modalidade e pagamento">
              <div className="mb-5 flex flex-wrap items-center gap-2">
                <QueueBadge request={request} />
                <PaymentBadge status={request.paymentStatus} />
              </div>
              <InfoGrid
                items={[
                  ['Modalidade', request.serviceLevel === 'PRIORITY' ? 'Rápida · prioritária' : 'Lenta · gratuita'],
                  ['Valor contratado', request.priorityFeeAmountCents == null ? 'Grátis' : formatMoney(request.priorityFeeAmountCents, request.priorityFeeCurrency)],
                  ['Entrada na fila', request.queuedAt ? new Date(request.queuedAt).toLocaleString('pt-BR') : 'Ainda não entrou'],
                  ['Pagamento', paymentLabel(request.paymentStatus)],
                ]}
              />
              {request.payments?.length > 0 && (
                <div className="mt-6 space-y-3 border-t border-[var(--dash-border)] pt-5">
                  {request.payments.map((payment) => (
                    <div key={payment.id} className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-surface-subtle)] p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <strong className="text-xs text-[var(--dash-text)]">Tentativa #{payment.id}</strong>
                        <PaymentBadge status={payment.status} />
                      </div>
                      <p className="mt-2 break-all text-[10px] text-[var(--dash-text-muted)]">{payment.stripeCheckoutSessionId}</p>
                      <p className="mt-2 text-[10px] text-[var(--dash-text-faint)]">
                        {formatMoney(payment.amountCents, payment.currency)} · criada em {new Date(payment.createdAt).toLocaleString('pt-BR')}
                        {payment.refundedAmountCents > 0 ? ` · reembolsado ${formatMoney(payment.refundedAmountCents, payment.currency)}` : ''}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </Section>
            <Section title="Agente e canais">
              <InfoGrid
                items={[
                  ['Nome do agente', request.agentName],
                  ['Canais', channels.map((channel) => CHANNEL_LABELS[channel] || channel).join(', ')],
                  ['Outro canal', request.otherChannel || 'Não informado'],
                ]}
              />
            </Section>
            <Section title="Missão do agente">
              <p className="whitespace-pre-wrap text-sm leading-7 text-[var(--dash-text-soft)]">{request.finalSummary}</p>
            </Section>
            {(request.knowledgeContext || knowledgeUrls.length > 0) && (
              <Section title="Conhecimento">
                <p className="whitespace-pre-wrap text-sm leading-7 text-[var(--dash-text-soft)]">{request.knowledgeContext || 'Nenhum contexto textual.'}</p>
                {knowledgeUrls.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {knowledgeUrls.map((item) => (
                      <a key={item} href={item} target="_blank" rel="noreferrer" className="block truncate text-xs text-[var(--dash-primary)] hover:underline">
                        {item}
                      </a>
                    ))}
                  </div>
                )}
              </Section>
            )}
            <Section title="Exemplos de conversa">
              <div className="space-y-4">
                {request.conversationExamples?.length ? (
                  request.conversationExamples.map((example, index) => (
                    <div key={example.id} className="rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface-subtle)] p-4">
                      <span className="mb-3 block text-[10px] font-semibold uppercase tracking-[0.15em] text-[var(--dash-text-faint)]">Exemplo {index + 1}</span>
                      <div className="space-y-3">
                        <Bubble label="Cliente" text={example.customerMessage} />
                        <Bubble label="Agente ideal" text={example.idealAgentResponse} accent />
                      </div>
                    </div>
                  ))
                ) : (
                  <EmptyText>Nenhum par de conversa informado.</EmptyText>
                )}
              </div>
            </Section>
            <Section title="Integrações">
              <div className="grid gap-3 sm:grid-cols-2">
                {request.integrations?.length ? (
                  request.integrations.map((integration) => (
                    <div key={integration.id} className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-surface-subtle)] p-4">
                      <strong className="text-xs text-[var(--dash-text)]">{INTEGRATION_LABELS[integration.name] || integration.name}</strong>
                      <p className="mt-2 text-xs leading-5 text-[var(--dash-text-muted)]">{integration.purpose || 'Sem detalhes adicionais.'}</p>
                    </div>
                  ))
                ) : (
                  <EmptyText>Nenhuma integração selecionada.</EmptyText>
                )}
              </div>
            </Section>
            <Section title="Anexos">
              <div className="space-y-2">
                {request.attachments?.length ? (
                  request.attachments.map((attachment) => (
                    <button key={attachment.id} onClick={() => download(attachment)} className="flex w-full items-center gap-3 rounded-xl border border-[var(--dash-border)] bg-[var(--dash-surface-subtle)] px-4 py-3 text-left hover:border-[var(--dash-border-strong)]">
                      <FileText size={16} className="text-[var(--dash-primary)]" />
                      <div className="min-w-0 flex-1">
                        <strong className="block truncate text-xs text-[var(--dash-text)]">{attachment.originalName}</strong>
                        <span className="mt-1 block text-[10px] text-[var(--dash-text-faint)]">
                          {attachment.kind === 'CONVERSATION_IMAGE' ? 'Screenshot de conversa' : 'Documento'} · {(attachment.size / 1024 / 1024).toFixed(1)} MB
                        </span>
                      </div>
                      <Download size={15} className="text-[var(--dash-text-muted)]" />
                    </button>
                  ))
                ) : (
                  <EmptyText>Nenhum anexo enviado.</EmptyText>
                )}
              </div>
            </Section>
            <Section title="Histórico do atendimento">
              <div className="space-y-4">
                {request.statusHistory?.map((event) => (
                  <div key={event.id} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[var(--dash-primary)]/20 bg-[var(--dash-primary)]/10 text-[var(--dash-primary)]">
                      <CheckCircle2 size={13} />
                    </span>
                    <div>
                      <Status status={event.status} />
                      <p className="mt-1.5 text-[10px] text-[var(--dash-text-faint)]">
                        {new Date(event.occurredAt).toLocaleString('pt-BR')}
                        {event.changedBy?.name ? ` · por ${event.changedBy.name}` : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
            <Section title="Entregas ao cliente">
              <div className="space-y-3">
                {request.notifications?.length ? (
                  request.notifications.map((notification) => (
                    <div key={notification.id} className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-surface-subtle)] p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <Bell size={14} className="text-[var(--dash-primary)]" />
                            <strong className="text-xs text-[var(--dash-text)]">{notification.channel === 'WHATSAPP' ? 'WhatsApp' : 'E-mail'}</strong>
                            <NotificationStatus status={notification.status} />
                          </div>
                          <p className="mt-2 break-all text-[10px] text-[var(--dash-text-muted)]">
                            {notification.recipient} · tentativa {notification.attempts}
                          </p>
                          {notification.lastError && <p className="mt-2 text-xs leading-5 text-rose-500">{notification.lastError}</p>}
                        </div>
                        {canRetryNotification(notification) && (
                          <Button size="sm" variant="outline" leftIcon={RefreshCw} loading={retrying === notification.id} onClick={() => retryNotification(notification.id)}>
                            Reenviar
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <EmptyText>Nenhuma entrega registrada.</EmptyText>
                )}
              </div>
            </Section>
          </div>
          <aside className="space-y-5">
            <div className="rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface)] p-5 shadow-[var(--dash-shadow-sm)]">
              <h2 className="text-sm font-semibold text-[var(--dash-text)]">Link do cliente</h2>
              <p className="mt-2 break-all text-[10px] leading-5 text-[var(--dash-text-muted)]">{trackingUrl}</p>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={Copy}
                  onClick={async () => {
                    await navigator.clipboard.writeText(trackingUrl);
                    setCopied(true);
                  }}
                >
                  {copied ? 'Copiado' : 'Copiar'}
                </Button>
                <Button variant="outline" size="sm" leftIcon={ExternalLink} onClick={() => window.open(trackingUrl, '_blank', 'noopener,noreferrer')}>
                  Abrir
                </Button>
              </div>
              {request.trackingExpiresAt && <p className="mt-3 text-[10px] text-[var(--dash-text-faint)]">Expira em {new Date(request.trackingExpiresAt).toLocaleString('pt-BR')}</p>}
            </div>
            <div className="sticky top-6 rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface)] p-5 shadow-[var(--dash-shadow-sm)]">
              <h2 className="text-sm font-semibold text-[var(--dash-text)]">Andamento</h2>
              <p className="mt-1 text-xs leading-5 text-[var(--dash-text-muted)]">{request.queuedAt ? 'Uma mudança real de status avisa o cliente por WhatsApp e e-mail.' : 'Aguardando pagamento. O status só poderá avançar depois que o pedido entrar em uma fila.'}</p>
              <label className="mt-5 block text-xs font-medium text-[var(--dash-text)]">
                Status
                <Select value={status} onChange={setStatus} disabled={!request.queuedAt} options={STATUSES.map(([value, label]) => ({ value, label }))} className="mt-2" />
              </label>
              <Button loading={savingStatus} onClick={saveStatus} disabled={!request.queuedAt || status === request.status} className="mt-4 w-full">
                Atualizar status e avisar
              </Button>
              <div className="my-6 h-px bg-[var(--dash-border)]" />
              <label className="block text-xs font-medium text-[var(--dash-text)]">
                Notas internas
                <Textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={7} maxLength={10000} showCount className="mt-2" placeholder="Contexto exclusivo para a equipe." />
              </label>
              <Button loading={savingNotes} onClick={saveNotes} variant="secondary" disabled={notes === (request.adminNotes || '')} className="mt-4 w-full">
                Salvar notas sem notificar
              </Button>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function PrioritySettingsDialog({ open, onClose }) {
  const [enabled, setEnabled] = useState(false);
  const [amount, setAmount] = useState('');
  const [setting, setSetting] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError('');
    setSaved('');
    getAgentBuildRequestSettings()
      .then((response) => {
        const data = unwrap(response);
        setSetting(data);
        setEnabled(!!data.priorityEnabled);
        setAmount(data.priorityFeeAmountCents == null ? '' : String(data.priorityFeeAmountCents / 100));
      })
      .catch((requestError) => setError(errorMessage(requestError, 'Não foi possível carregar a configuração.')))
      .finally(() => setLoading(false));
  }, [open]);

  const save = async () => {
    const amountCents = amount === '' ? null : Math.round(Number(amount) * 100);
    if (enabled && (!Number.isFinite(amountCents) || amountCents < 50)) {
      setError('Informe um valor de pelo menos R$ 0,50.');
      return;
    }
    setSaving(true);
    setError('');
    setSaved('');
    try {
      const response = await updateAgentBuildRequestSettings({
        priorityEnabled: enabled,
        priorityFeeAmountCents: amountCents,
      });
      const data = unwrap(response);
      setSetting(data);
      setSaved(enabled ? 'Modalidade Rápida publicada com o novo valor.' : 'Modalidade Rápida desativada para novos briefings.');
    } catch (requestError) {
      setError(errorMessage(requestError, 'Não foi possível salvar a configuração.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <DialogHeader icon={Settings2} title="Configurar modalidade Rápida" description="Este é o valor global da taxa única de prioridade. Alterações valem somente para novos briefings." />
      <DialogContent>
        {loading ? (
          <div className="h-36 animate-pulse rounded-xl bg-[var(--dash-surface-subtle)]" />
        ) : (
          <div className="space-y-5">
            <div className="flex items-center justify-between gap-5 rounded-xl border border-[var(--dash-border)] bg-[var(--dash-surface-subtle)] p-4">
              <div>
                <strong className="text-sm text-[var(--dash-text)]">Oferecer fila prioritária</strong>
                <p className="mt-1 text-xs leading-5 text-[var(--dash-text-muted)]">Quando desligada, o formulário informa que a opção está temporariamente indisponível.</p>
              </div>
              <Switch checked={enabled} onChange={setEnabled} />
            </div>
            <label className="block text-xs font-medium text-[var(--dash-text)]">
              Taxa única em reais
              <Input value={amount} onChange={(event) => setAmount(event.target.value)} leftIcon={CreditCard} type="number" min="0.5" step="0.01" placeholder="Ex.: 500,00" className="mt-2" />
            </label>
            <p className="text-[10px] leading-5 text-[var(--dash-text-faint)]">Pedidos já enviados preservam o preço contratado, mesmo após uma alteração ou desativação.</p>
            {setting?.updatedAt && (
              <p className="text-[10px] text-[var(--dash-text-faint)]">
                Última alteração em {new Date(setting.updatedAt).toLocaleString('pt-BR')}
                {setting.updatedBy?.name ? ` por ${setting.updatedBy.name}` : ''}.
              </p>
            )}
            {error && <ErrorBox>{error}</ErrorBox>}
            {saved && <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3 text-xs text-emerald-600">{saved}</div>}
          </div>
        )}
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Fechar
        </Button>
        <Button loading={saving} disabled={loading} onClick={save}>
          Salvar configuração
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function QueueBadge({ request }) {
  const waiting = request.serviceLevel === 'PRIORITY' && !request.queuedAt;
  const priority = request.serviceLevel === 'PRIORITY' && !!request.queuedAt;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.08em] ${waiting ? 'bg-amber-400/10 text-amber-500' : priority ? 'bg-fuchsia-400/10 text-fuchsia-500' : 'bg-emerald-400/10 text-emerald-500'}`}>
      {priority && <Zap size={10} />}
      {waiting ? 'Aguardando pagamento' : priority ? 'Prioritária' : 'Gratuita'}
    </span>
  );
}

function PaymentBadge({ status }) {
  const label = paymentLabel(status);
  const positive = ['PAID', 'PARTIALLY_REFUNDED', 'REFUNDED'].includes(status);
  const negative = ['FAILED', 'EXPIRED'].includes(status);
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-[9px] font-semibold ${positive ? 'bg-emerald-400/10 text-emerald-500' : negative ? 'bg-rose-400/10 text-rose-500' : 'bg-amber-400/10 text-amber-500'}`}>{label}</span>;
}

function paymentLabel(status) {
  return (
    {
      NOT_REQUIRED: 'Não necessário',
      PENDING: 'Pendente',
      OPEN: 'Checkout aberto',
      PAID: 'Pago',
      FAILED: 'Falhou',
      EXPIRED: 'Expirou',
      PARTIALLY_REFUNDED: 'Reembolso parcial',
      REFUNDED: 'Reembolsado',
    }[status] ||
    status ||
    'Não informado'
  );
}

function formatMoney(amount, currency = 'BRL') {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency || 'BRL',
  }).format((amount || 0) / 100);
}

function Status({ status }) {
  return <span className={`inline-flex w-fit rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] ${STATUS_STYLE[status] || STATUS_STYLE.PENDING}`}>{STATUSES.find(([value]) => value === status)?.[1] || status}</span>;
}
function NotificationStatus({ status }) {
  const failed = status === 'FAILED';
  const sent = status === 'SENT';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-semibold ${failed ? 'bg-rose-400/10 text-rose-500' : sent ? 'bg-emerald-400/10 text-emerald-500' : 'bg-amber-400/10 text-amber-500'}`}>
      {failed ? <XCircle size={10} /> : sent ? <CheckCircle2 size={10} /> : <RefreshCw size={10} />}
      {failed ? 'Falhou' : sent ? 'Enviado' : status === 'PROCESSING' ? 'Processando' : 'Pendente'}
    </span>
  );
}
function canRetryNotification(notification) {
  return notification.status === 'FAILED' || notification.status === 'PENDING' || (notification.status === 'PROCESSING' && Date.now() - new Date(notification.updatedAt).getTime() > 120000);
}
function Section({ title, children }) {
  return (
    <section className="rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface)] p-5 shadow-[var(--dash-shadow-sm)] sm:p-6">
      <h2 className="mb-5 text-sm font-semibold text-[var(--dash-text)]">{title}</h2>
      {children}
    </section>
  );
}
function InfoGrid({ items }) {
  return (
    <dl className="grid gap-5 sm:grid-cols-2">
      {items.map(([label, value]) => (
        <div key={label}>
          <dt className="text-[10px] uppercase tracking-[0.14em] text-[var(--dash-text-faint)]">{label}</dt>
          <dd className="mt-1.5 break-words text-sm font-medium text-[var(--dash-text-soft)]">{String(value)}</dd>
        </div>
      ))}
    </dl>
  );
}
function Bubble({ label, text, accent }) {
  return (
    <div className={`max-w-[88%] rounded-2xl px-4 py-3 ${accent ? 'ml-auto bg-[var(--dash-primary)] text-white' : 'border border-[var(--dash-border)] bg-[var(--dash-surface)] text-[var(--dash-text-soft)]'}`}>
      <span className={`block text-[9px] font-semibold uppercase tracking-[0.12em] ${accent ? 'text-white/60' : 'text-[var(--dash-text-faint)]'}`}>{label}</span>
      <p className="mt-1 whitespace-pre-wrap text-xs leading-5">{text}</p>
    </div>
  );
}
function EmptyText({ children }) {
  return <p className="text-xs text-[var(--dash-text-muted)]">{children}</p>;
}
function ErrorBox({ children }) {
  return <div className="mb-5 rounded-xl border border-rose-400/20 bg-rose-400/10 px-4 py-3 text-xs text-rose-500">{children}</div>;
}
